/**
 * QXO filings desk — datasets hand-extracted from SEC filings and earnings releases.
 *
 * Every number here has a filing behind it. Sources (all on EDGAR):
 *   - QXO 10-Q for Q2 2026 (filed 2026-08-13, CIK 1236275)
 *   - QXO 10-K for FY2025
 *   - QXO earnings releases: Q3 25 (Ex. 99.1), Q4 25, Q1 26, Q2 26
 *   - QXO 8-K 2026-07-01 (TopBuild close: notes, incremental term loan, Series C)
 *   - QXO 8-K 2026-07-09 (Article 11 pro forma for TopBuild + Kodiak)
 *   - Beacon Roofing Supply 8-K earnings releases FY2024 (CIK 1124941) and Beacon
 *     predecessor stub (Jan 1 – Apr 28, 2025) in QXO's 8-K/A
 *   - TopBuild and Kodiak historical financials as filed in QXO's S-4 / 8-K/A
 *
 * Anything that is a derivation (not a filed line) is marked `derived: true` or
 * lives in qxoCapitalMath.ts so it can be unit-tested and challenged.
 */

export const FILINGS_DESK_AS_OF = 'Filings through 13 Aug 2026 (Q2 26 10-Q)';

export type PerimeterTag = 'QXO reported' | 'Beacon standalone' | 'Beacon predecessor stub';

export interface QuarterRow {
  period: string;
  /** Who filed the number. */
  perimeter: PerimeterTag;
  netSales: number;
  /** Adjusted gross margin where QXO reports it (strips inventory step-up); GAAP for Beacon. */
  grossMarginPct: number | null;
  sgaM: number | null;
  adjEbitda: number | null;
  adjEbitdaMarginPct: number | null;
  adjDilutedEps: number | null;
  cfoM: number | null;
  capexM: number | null;
  note?: string;
}

/**
 * QXO as reported. Beacon entered on 29 Apr 2025, Kodiak on 1 Apr 2026, TopBuild on 1 Jul 2026
 * (so Q3 26 will be the first quarter with all three).
 * CFO/capex per quarter are backed out of the YTD cash-flow statements.
 */
export const QXO_QUARTERS: QuarterRow[] = [
  {
    period: 'Q2 25',
    perimeter: 'QXO reported',
    netSales: 1906,
    grossMarginPct: 25.3,
    sgaM: 457,
    adjEbitda: 204,
    adjEbitdaMarginPct: 10.7,
    adjDilutedEps: 0.11,
    cfoM: -174.5,
    capexM: 19.9,
    note: 'Beacon for 63 days only (Apr 29 – Jun 30).',
  },
  {
    period: 'Q3 25',
    perimeter: 'QXO reported',
    netSales: 2728.3,
    grossMarginPct: 25.2,
    sgaM: 452.5,
    adjEbitda: 301.9,
    adjEbitdaMarginPct: 11.1,
    adjDilutedEps: 0.14,
    cfoM: 212.8,
    capexM: 29.8,
    note: 'First full Beacon quarter. GAAP gross margin 23.3% (inventory step-up).',
  },
  {
    period: 'Q4 25',
    perimeter: 'QXO reported',
    netSales: 2194.1,
    grossMarginPct: 24.2,
    sgaM: 441.1,
    adjEbitda: 150.3,
    adjEbitdaMarginPct: 6.9,
    adjDilutedEps: 0.02,
    cfoM: 186.6,
    capexM: 28.4,
  },
  {
    period: 'Q1 26',
    perimeter: 'QXO reported',
    netSales: 1730.2,
    grossMarginPct: 23.7,
    sgaM: 497.0,
    adjEbitda: 1.2,
    adjEbitdaMarginPct: 0.1,
    adjDilutedEps: -0.12,
    cfoM: 70.6,
    capexM: 22.5,
    note: 'Seasonal trough plus SG&A running $56M above Q4 with $464M less revenue.',
  },
  {
    period: 'Q2 26',
    perimeter: 'QXO reported',
    netSales: 3246,
    grossMarginPct: 24.7,
    sgaM: 649,
    adjEbitda: 272,
    adjEbitdaMarginPct: 8.4,
    adjDilutedEps: 0.08,
    cfoM: -216.6,
    capexM: 31.5,
    note: 'Includes $595M Kodiak revenue (first Kodiak quarter). TopBuild not yet in.',
  },
];

/** Beacon Roofing Supply as it reported before the takeover, plus the predecessor stub QXO filed. */
export const BEACON_QUARTERS: QuarterRow[] = [
  { period: 'Q1 24', perimeter: 'Beacon standalone', netSales: 1912.4, grossMarginPct: null, sgaM: null, adjEbitda: 103.1, adjEbitdaMarginPct: 5.4, adjDilutedEps: null, cfoM: null, capexM: null },
  { period: 'Q2 24', perimeter: 'Beacon standalone', netSales: 2674.6, grossMarginPct: null, sgaM: null, adjEbitda: 279.4, adjEbitdaMarginPct: 10.4, adjDilutedEps: null, cfoM: null, capexM: null },
  { period: 'Q3 24', perimeter: 'Beacon standalone', netSales: 2772.6, grossMarginPct: 26.3, sgaM: null, adjEbitda: 325.2, adjEbitdaMarginPct: 11.7, adjDilutedEps: null, cfoM: null, capexM: null },
  { period: 'Q4 24', perimeter: 'Beacon standalone', netSales: 2403.6, grossMarginPct: 25.7, sgaM: null, adjEbitda: 222.5, adjEbitdaMarginPct: 9.3, adjDilutedEps: null, cfoM: null, capexM: null },
  { period: 'FY 24', perimeter: 'Beacon standalone', netSales: 9763.2, grossMarginPct: 25.7, sgaM: null, adjEbitda: 930.2, adjEbitdaMarginPct: 9.5, adjDilutedEps: null, cfoM: null, capexM: null },
  {
    period: 'Jan 1 – Apr 28, 25',
    perimeter: 'Beacon predecessor stub',
    netSales: 2694.6,
    grossMarginPct: null,
    sgaM: null,
    adjEbitda: 135.2,
    adjEbitdaMarginPct: 5.0,
    adjDilutedEps: null,
    cfoM: null,
    capexM: null,
    note: 'Predecessor period QXO filed. Q1 25 alone was $1,907.8M of sales.',
  },
];

/**
 * Same-perimeter comparison: what the Beacon business did under QXO vs the same quarter under Beacon.
 * QXO net sales are shown ex-software (legacy QXO ~$14–15M/qtr) and ex-Kodiak so the perimeter matches.
 */
export interface LikeForLikeRow {
  period: string;
  beaconYearAgoSales: number;
  qxoPerimeterSales: number;
  salesYoyPct: number;
  beaconYearAgoEbitda: number | null;
  qxoPerimeterEbitda: number | null;
  ebitdaYoyPct: number | null;
  beaconYearAgoMarginPct: number | null;
  qxoPerimeterMarginPct: number | null;
  derived: true;
  note: string;
}

export const BEACON_LIKE_FOR_LIKE: LikeForLikeRow[] = [
  {
    period: 'Q3 25 vs Q3 24',
    beaconYearAgoSales: 2772.6,
    qxoPerimeterSales: 2714.2,
    salesYoyPct: -2.1,
    beaconYearAgoEbitda: 325.2,
    qxoPerimeterEbitda: 301.9,
    ebitdaYoyPct: -7.2,
    beaconYearAgoMarginPct: 11.7,
    qxoPerimeterMarginPct: 11.1,
    derived: true,
    note: 'QXO EBITDA includes ~$14M legacy software revenue; immaterial to the margin.',
  },
  {
    period: 'Q4 25 vs Q4 24',
    beaconYearAgoSales: 2403.6,
    qxoPerimeterSales: 2178.1,
    salesYoyPct: -9.4,
    beaconYearAgoEbitda: 222.5,
    qxoPerimeterEbitda: 150.3,
    ebitdaYoyPct: -32.4,
    beaconYearAgoMarginPct: 9.3,
    qxoPerimeterMarginPct: 6.9,
    derived: true,
    note: 'Beacon\u2019s Q4 24 was its record Q4. QXO flagged softer volumes and price.',
  },
  {
    period: 'Q1 26 vs Q1 25',
    beaconYearAgoSales: 1907.8,
    qxoPerimeterSales: 1715.6,
    salesYoyPct: -10.1,
    beaconYearAgoEbitda: null,
    qxoPerimeterEbitda: 1.2,
    ebitdaYoyPct: null,
    beaconYearAgoMarginPct: 5.0,
    qxoPerimeterMarginPct: 0.1,
    derived: true,
    note: 'Beacon never filed a standalone Q1 25 EBITDA; 5.0% is the Jan 1 – Apr 28 stub margin. Q1 24 Beacon was 5.4%.',
  },
  {
    period: 'Q2 26 vs Q2 25',
    beaconYearAgoSales: 2678.8,
    qxoPerimeterSales: 2636,
    salesYoyPct: -1.6,
    beaconYearAgoEbitda: null,
    qxoPerimeterEbitda: null,
    ebitdaYoyPct: null,
    beaconYearAgoMarginPct: 10.4,
    qxoPerimeterMarginPct: null,
    derived: true,
    note: 'Year-ago = QXO Q2 25 ex-software ($1,892M) + Beacon Apr 1–28 stub ($786.8M). Q2 26 strips $595M Kodiak and ~$15M software. QXO does not split Q2 26 EBITDA by business, so margin is blank; 10.4% shown is Beacon Q2 24.',
  },
];

/** Product-line net sales, Beacon perimeter, $M. Rows are aligned so same-quarter columns are like-for-like. */
export interface ProductLineRow {
  period: string;
  perimeter: PerimeterTag;
  residential: number;
  nonResidential: number;
  complementary: number;
  note?: string;
}

export const PRODUCT_LINES: ProductLineRow[] = [
  { period: 'Q1 24', perimeter: 'Beacon standalone', residential: 927.4, nonResidential: 528.6, complementary: 456.4 },
  { period: 'Q2 24', perimeter: 'Beacon standalone', residential: 1328.9, nonResidential: 745.1, complementary: 600.6 },
  { period: 'Q3 24', perimeter: 'Beacon standalone', residential: 1404.9, nonResidential: 739.0, complementary: 628.7 },
  { period: 'Q4 24', perimeter: 'Beacon standalone', residential: 1172.3, nonResidential: 661.4, complementary: 569.9 },
  { period: 'Q1 25', perimeter: 'Beacon predecessor stub', residential: 928.6, nonResidential: 501.3, complementary: 477.9 },
  { period: 'Q3 25', perimeter: 'QXO reported', residential: 1351.7, nonResidential: 734.0, complementary: 628.5 },
  { period: 'Q1 26', perimeter: 'QXO reported', residential: 799.1, nonResidential: 463.6, complementary: 452.9 },
  {
    period: 'Q2 26',
    perimeter: 'QXO reported',
    residential: 1266,
    nonResidential: 736,
    complementary: 634,
    note: 'Complementary shown ex-Kodiak ($1,229M reported less $595M Kodiak). Q2 25 and Q4 25 product lines were not disclosed in a comparable form.',
  },
];

export const PRODUCT_LINE_YOY = [
  { line: 'Residential roofing', q3: -3.8, q1: -13.9, q2TwoYear: -4.7 },
  { line: 'Non-residential roofing', q3: -0.7, q1: -7.5, q2TwoYear: -1.2 },
  { line: 'Complementary (ex-Kodiak)', q3: 0.0, q1: -5.2, q2TwoYear: 5.6 },
] as const;

/** FY2025 baseline for the three acquired platforms, as filed, against the 2030 plan. */
export interface PlatformBaseline {
  platform: string;
  closed: string;
  priceB: number;
  fy25Sales: number;
  fy25AdjEbitda: number;
  fy25MarginPct: number;
  paidMultiple: number;
  target2030EbitdaB: number;
  source: string;
}

export const PLATFORM_BASELINE: PlatformBaseline[] = [
  {
    platform: 'Beacon (roofing distribution)',
    closed: '29 Apr 2025',
    priceB: 10.64,
    fy25Sales: 9536.8,
    fy25AdjEbitda: 783.0,
    fy25MarginPct: 8.2,
    paidMultiple: 11.4,
    target2030EbitdaB: 2.0,
    source: 'Predecessor stub + QXO Q2–Q4 25 ex-software. Paid multiple on Beacon FY24 EBITDA $930M.',
  },
  {
    platform: 'Kodiak (lumber / building materials)',
    closed: '1 Apr 2026',
    priceB: 2.22,
    fy25Sales: 2337.8,
    fy25AdjEbitda: 204.7,
    fy25MarginPct: 8.8,
    paidMultiple: 10.8,
    target2030EbitdaB: 0.4,
    source: 'Kodiak FY25 per QXO 8-K/A. $257M of price in QXO stock (13.3M shares).',
  },
  {
    platform: 'TopBuild (insulation installer)',
    closed: '1 Jul 2026',
    priceB: 15.0,
    fy25Sales: 6214.1,
    fy25AdjEbitda: 1141.9,
    fy25MarginPct: 18.4,
    paidMultiple: 13.2,
    target2030EbitdaB: 1.6,
    source: 'TopBuild FY25 incl. its own 2025 bolt-ons. ~312M QXO shares at $18.44 reference + cash.',
  },
];

/** QXO's Article 11 pro forma for FY2025 (8-K, 9 Jul 2026), all three platforms, $M. */
export const PRO_FORMA_FY25 = {
  netSales: 17283.7,
  grossProfit: 4457.8,
  grossMarginPct: 25.8,
  sga: 3241.5,
  depreciation: 210.9,
  amortization: 1217.6,
  operatingLoss: -212.2,
  interestExpense: -496.3,
  pretaxLoss: -745.5,
  netLoss: -587.1,
  preferredDividends: -264.1,
  lossToCommon: -851.2,
  weightedShares: 1071.7,
  gaapEps: -0.79,
  /** Sum of the three platform FY25 adjusted EBITDA figures above (not an Article 11 line). */
  combinedAdjEbitda: 2129.6,
  derived: { combinedAdjEbitda: true },
} as const;

/** Post-TopBuild funded debt, from the Q2 26 10-Q and the 1 Jul 2026 8-K. */
export interface DebtTranche {
  name: string;
  principalM: number;
  ratePct: number;
  rateType: 'fixed' | 'floating';
  maturity: string;
  secured: boolean;
  note?: string;
}

export const DEBT_STACK: DebtTranche[] = [
  { name: 'ABL revolver', principalM: 11, ratePct: 4.7, rateType: 'floating', maturity: 'Apr 2030', secured: true, note: '$1.93B borrowing capacity at Jun 30; effectively undrawn.' },
  { name: 'Term Loan Facility (Beacon)', principalM: 850, ratePct: 5.64, rateType: 'floating', maturity: 'Apr 2032', secured: true, note: 'SOFR + 2.00% after the 2025 repricing; partially prepaid with May 2025 equity ($46M extinguishment loss).' },
  { name: 'Incremental Term Loan (TopBuild)', principalM: 3000, ratePct: 5.75, rateType: 'floating', maturity: 'Jul 2033', secured: true, note: 'SOFR + margin; the 5.75% shown is an estimate pending the first 10-Q disclosure. 1% annual amortization.' },
  { name: '6.500% Senior Notes', principalM: 1500, ratePct: 6.5, rateType: 'fixed', maturity: 'Jul 2031', secured: false, note: 'Issued Jun 2026 into escrow for TopBuild.' },
  { name: '6.750% Senior Secured Notes', principalM: 2250, ratePct: 6.75, rateType: 'fixed', maturity: 'Apr 2032', secured: true, note: 'Beacon acquisition notes.' },
  { name: '6.875% Senior Notes', principalM: 1500, ratePct: 6.875, rateType: 'fixed', maturity: 'Jul 2034', secured: false, note: 'Issued Jun 2026 into escrow for TopBuild.' },
];

/** Preferred layers senior to common, from the 10-Q equity note and the Series C certificate. */
export interface PreferredLayer {
  name: string;
  holders: string;
  liquidationM: number;
  couponPct: number;
  annualDividendM: number;
  conversionPrice: number | null;
  /** Shares of common on full conversion, millions. */
  conversionSharesM: number;
  conversionTerms: string;
}

export const PREFERRED_STACK: PreferredLayer[] = [
  {
    name: 'Series A Convertible Preferred',
    holders: 'Jacobs Private Equity + 2024 co-investors',
    liquidationM: 1000,
    couponPct: 9.0,
    annualDividendM: 90,
    conversionPrice: 4.566,
    conversionSharesM: 219.0,
    conversionTerms: 'Convertible at holder option at $4.566; deep in the money. Carried at $499M.',
  },
  {
    name: '5.50% Series B Mandatory Convertible',
    holders: 'Public (11.5M depositary shares, $50 each)',
    liquidationM: 575,
    couponPct: 5.5,
    annualDividendM: 31.6,
    conversionPrice: 16.5,
    conversionSharesM: 34.8,
    conversionTerms: 'Mandatory on 15 May 2028: 49.474–60.606 shares per $1,000 (28.4M shares above $20.21, 34.8M below $16.50).',
  },
  {
    name: '4.75% Series C Convertible Preferred',
    holders: 'Apollo, Temasek and others (TopBuild funding)',
    liquidationM: 3000,
    couponPct: 4.75,
    annualDividendM: 142.5,
    conversionPrice: 23.25,
    conversionSharesM: 129.0,
    conversionTerms: 'Converts at $23.25. Until then a $3.0B claim ahead of common; QXO paid $23M of Series C dividends in cash in Q2 26.',
  },
];

/** Warrants issued with the 2024 Jacobs placement; exercisable to Jun 2034. */
export const WARRANTS = [
  { tranche: 'Warrants — tranche 1', sharesM: 109.5, strike: 4.566 },
  { tranche: 'Warrants — tranche 2', sharesM: 54.75, strike: 6.849 },
  { tranche: 'Warrants — tranche 3', sharesM: 54.75, strike: 13.698 },
] as const;

export const SHARE_COUNT = {
  asOf: '7 Aug 2026',
  commonOutstandingM: 1037.5,
  /** Exercise price $0.00001; the company counts these in basic EPS. */
  preFundedWarrantsM: 42.0,
} as const;

/** Every price at which Jacobs sold equity, from the 10-Q equity note and deal 8-Ks. */
export interface EquityEvent {
  date: string;
  event: string;
  price: number;
  sharesM: number;
  grossM: number;
  kind: 'private' | 'public' | 'acquisition' | 'reference';
}

export const EQUITY_LADDER: EquityEvent[] = [
  { date: 'Jun–Jul 2024', event: 'Jacobs Private Equity + co-investor placement (incl. 42M pre-funded warrants)', price: 9.14, sharesM: 408.7, grossM: 3736, kind: 'private' },
  { date: 'Mar–Apr 2025', event: 'Private placement (Beacon funding)', price: 12.3, sharesM: 67.5, grossM: 830, kind: 'private' },
  { date: 'Apr 2025', event: 'Public offering (Beacon funding)', price: 13.25, sharesM: 41.7, grossM: 553, kind: 'public' },
  { date: 'May 2025', event: 'Public offering + Series B mandatory convertible', price: 16.5, sharesM: 55.8, grossM: 921, kind: 'public' },
  { date: 'Jun 2025', event: 'Public offering', price: 22.25, sharesM: 91.6, grossM: 2038, kind: 'public' },
  { date: 'Jan 2026', event: 'Public offering', price: 23.8, sharesM: 31.6, grossM: 752, kind: 'public' },
  { date: 'Apr 2026', event: 'Kodiak sellers (stock portion)', price: 19.3, sharesM: 13.3, grossM: 257, kind: 'acquisition' },
  { date: 'Jul 2026', event: 'TopBuild shareholders (pro forma reference price)', price: 18.44, sharesM: 312.0, grossM: 5753, kind: 'acquisition' },
  { date: 'Jul 2026', event: 'Series C conversion price (Apollo / Temasek)', price: 23.25, sharesM: 129.0, grossM: 3000, kind: 'reference' },
];

/** Working capital, consolidated QXO, $M. Q2 26 includes Kodiak; TopBuild not yet consolidated. */
export const WORKING_CAPITAL = [
  { date: 'Sep 30 25', receivables: 1562.4, inventory: 1663.8, rebates: 496.8, payables: 1277.7, cash: 2306.9 },
  { date: 'Dec 31 25', receivables: 1145.1, inventory: 1497.3, rebates: 427.0, payables: 819.0, cash: 2361.6 },
  { date: 'Mar 31 26', receivables: 1135.7, inventory: 1668.2, rebates: 478.8, payables: 1170.8, cash: 3046.3 },
  { date: 'Jun 30 26', receivables: 1782, inventory: 2072, rebates: 531, payables: 1505, cash: 2774 },
] as const;

/** Preferred dividends actually paid in cash, $M, from the cash-flow statement. */
export const PREFERRED_CASH_DIVIDENDS = [
  { period: 'Q3 25', paid: 29.4 },
  { period: 'Q4 25', paid: 30.4 },
  { period: 'Q1 26', paid: 30.4 },
  { period: 'Q2 26', paid: 53.6 },
] as const;

/** Things management said on the record that can be checked against later prints. */
export interface ProofPoint {
  claim: string;
  saidWhen: string;
  checkAgainst: string;
  status: 'pending' | 'on track' | 'missed' | 'partial';
}

export const MANAGEMENT_PROOF_POINTS: ProofPoint[] = [
  {
    claim: '$2.0B adjusted EBITDA from the Beacon platform by 2030 (from $0.78B in FY25).',
    saidWhen: 'May 2026 investor day; reiterated Q2 26 release',
    checkAgainst: 'Beacon-perimeter EBITDA margin. Needs ~21% on flat revenue or ~14% on $14B. Beacon never printed above 11.7% in a quarter.',
    status: 'pending',
  },
  {
    claim: '$4.0B combined 2030 EBITDA target, "organic, before further M&A".',
    saidWhen: 'Q2 26 release, 13 Aug 2026',
    checkAgainst: 'FY25 combined base is $2.13B. Implies ~13% CAGR while the Beacon perimeter shrank 16% in FY25.',
    status: 'pending',
  },
  {
    claim: 'Gross margin expansion from private label, pricing discipline and purchasing scale.',
    saidWhen: 'Investor day and every call since Q3 25',
    checkAgainst: 'Adjusted GM: Q3 25 25.2%, Q4 25 24.2%, Q1 26 23.7%, Q2 26 24.7% vs Beacon FY24 25.7% (Q3 24 26.3%). Moving the wrong way so far.',
    status: 'missed',
  },
  {
    claim: 'Branch-level pricing tools and rebate capture as the first margin lever.',
    saidWhen: 'Q3 25 and Q4 25 calls',
    checkAgainst: 'Vendor rebates receivable was $531M at Jun 30 26 vs $497M at Sep 30 25 on similar inventory; watch rebates / inventory.',
    status: 'partial',
  },
  {
    claim: 'TopBuild synergies and cross-sell (insulation into roofing branches) with no revenue dis-synergy.',
    saidWhen: '1 Jul 2026 close release',
    checkAgainst: 'TopBuild installation same-branch sales in Q3 26, first consolidated quarter. TopBuild FY25 margin was 18.4% — keep it.',
    status: 'pending',
  },
  {
    claim: 'SG&A to grow slower than revenue once integration costs roll off.',
    saidWhen: 'Q4 25 call',
    checkAgainst: 'SG&A / net sales: Q3 25 16.6%, Q4 25 20.1%, Q1 26 28.7%, Q2 26 20.0%. Beacon\u2019s adjusted opex ran ~18% of sales in Q4 24.',
    status: 'missed',
  },
];

/** Deal multiples vs where the stock trades. Peer multiples come from the street snapshot when live. */
export const DEAL_MULTIPLES = [
  { name: 'Beacon (paid, Apr 25)', evM: 10640, ebitdaM: 930.2, basis: 'FY24 adj. EBITDA' },
  { name: 'Kodiak (paid, Apr 26)', evM: 2220, ebitdaM: 204.7, basis: 'FY25 adj. EBITDA' },
  { name: 'TopBuild (paid, Jul 26)', evM: 15000, ebitdaM: 1141.9, basis: 'FY25 adj. EBITDA' },
] as const;

export const FILINGS_SOURCES = [
  'QXO Form 10-Q for the quarter ended 30 Jun 2026 (filed 13 Aug 2026): cover share count, debt note, mezzanine equity note, warrants, EPS note, cash-flow statement.',
  'QXO Form 10-K FY2025: FY25 income statement, Beacon purchase accounting, Series A/B terms.',
  'QXO Form 8-K 9 Jul 2026 (Ex. 99.1–99.3): Article 11 unaudited pro forma combined FY2025 for Beacon + Kodiak + TopBuild.',
  'QXO Form 8-K 1 Jul 2026: TopBuild closing, $3.0B incremental term loan, $5.25B notes, $3.0B Series C.',
  'QXO earnings releases Q3 25, Q4 25, Q1 26, Q2 26 (Ex. 99.1) for adjusted EBITDA, adjusted GM, adjusted EPS and product-line sales.',
  'Beacon Roofing Supply Form 8-K earnings releases for Q1–Q4 2024 and FY2024 (CIK 1124941); Beacon predecessor stub (1 Jan – 28 Apr 2025) from QXO Form 8-K/A.',
] as const;
